import { display, type DateShape } from './dateDisplay';

/**
 * Comparing a date-only due date, on the client, without losing a day.
 *
 * `new Date("2026-08-26")` is parsed by the spec as **UTC midnight**, not local
 * midnight. Comparing that to `Date.now()` in Asia/Kolkata makes the boundary
 * 05:30 local, so a PPM schedule due today read as overdue from half past five
 * in the morning — a full day early — and the batch-create preview offered to
 * generate work orders for it. Dashboard BUG_02, the client half.
 *
 * The backend answers the same question against the ORGANISATION's midnight
 * (utils/org_time.org_today). The browser cannot know that zone without being
 * told, so these compare CALENDAR DAYS in the viewer's own frame: for a
 * date-only value that is the closest honest reading, and it is never a day
 * out the way UTC-midnight-vs-local-now is.
 */

/** A "YYYY-MM-DD" string as a Date at LOCAL midnight, not UTC midnight. */
export function parseDueDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  // Anchored at BOTH ends: a full timestamp carries its own offset and must
  // be parsed as-is. Matching only the prefix threw the time away and shifted
  // "2026-08-26T18:30:00Z" back a day.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) {
    // A full timestamp carries its own offset and is unambiguous.
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Today at local midnight — the thing a due date is compared against. */
export function localToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Whole days from today until `value`. Negative once past due.
 *
 * Signed, matching `overdue_state().days_until_due` on the server, so the two
 * halves of the product describe lateness the same way.
 */
export function daysUntilDue(value: string | null | undefined): number | null {
  const due = parseDueDate(value);
  if (!due) return null;
  return Math.round((due.getTime() - localToday().getTime()) / 86400000);
}

/**
 * Past its due date.
 *
 * Strictly before today, so something due TODAY is not yet overdue — the same
 * end-of-day boundary as `due_date < today` on the server and
 * `wo_due_deadline()` in the scheduler.
 */
export function isPastDue(value: string | null | undefined): boolean {
  const d = daysUntilDue(value);
  return d !== null && d < 0;
}

/**
 * A date on screen. The SHAPE is the caller's, the spelling is the reader's.
 *
 * 🔴 THIS TOOK A DATE-FNS PATTERN, AND THAT IS WHY A PREFERENCE COULD NOT
 * REACH IT. Fifty-two call sites each spelled their own — 'd MMM yyyy',
 * 'MMM d, yyyy' and 'dd MMM yyyy' were all in use for the same thing — so the
 * app rendered one kind of date three ways and `users.date_format` had nowhere
 * to apply. A shape says what the date is FOR; how it is spelled is one
 * decision, taken in utils/dateDisplay against the account's pattern.
 *
 * The day-shifting bug this function was written for is still handled, and now
 * in one place rather than two: `format(new Date("2026-08-26"), "MMM d")`
 * shows **Aug 25** to anyone west of Greenwich, because the string parses to
 * UTC midnight and renders in a zone behind it. dateDisplay reads a bare
 * `YYYY-MM-DD` literally — a calendar date carries no zone — and renders a
 * real timestamp in the ORGANISATION's.
 */
export function formatDate(
  value: string | null | undefined | Date,
  shape: DateShape,
  fallback = '—',
): string {
  return display(value, shape, { fallback });
}

// ---------------------------------------------------------------------------
// Warranty
// ---------------------------------------------------------------------------

/** Where an asset stands against its warranty date. */
export type WarrantyState = 'under' | 'expired' | 'pending' | 'none';

/**
 * 🔴 ONE DERIVATION, SO TWO SCREENS CANNOT DISAGREE. Warranty is not a stored
 * string on the asset — there is only `warranty_expiry`, a date — so every
 * surface that shows it is deciding for itself. The Assets list already made
 * this call with `!isPastDue(expiry)`; the scan card made none at all and
 * showed nothing. Both now ask here.
 *
 * `isPastDue` rather than `new Date(expiry) > new Date()`: a date-only string
 * parses to UTC midnight, so a warranty expiring today read as already lapsed
 * for the first five and a half hours of an IST day.
 */
export function warrantyState(
  expiry: string | null | undefined,
  status?: string | null,
): WarrantyState {
  if (!expiry) return 'none';
  // An ordered asset is not under warranty yet: cover starts when it arrives.
  // The date is still worth showing — a vendor's expiry is often known at
  // order time — but it is not "under", and the Assets list counts "under".
  if (status === 'on_order') return 'pending';
  return isPastDue(expiry) ? 'expired' : 'under';
}

/**
 * The full sentence for a surface with room for one — the scan card, where a
 * person is standing in front of the thing and a bare "Expired" does not say
 * how long ago. Narrow surfaces render the state instead; both come from
 * `warrantyState`, so they cannot contradict each other.
 */
export function warrantyLabel(
  expiry: string | null | undefined,
  status?: string | null,
): string {
  switch (warrantyState(expiry, status)) {
    case 'pending':
      return `Expires ${formatDate(expiry, 'dateLong')} (starts on delivery)`;
    case 'under':
      return `Under warranty until ${formatDate(expiry, 'dateLong')}`;
    case 'expired':
      return `Expired on ${formatDate(expiry, 'dateLong')}`;
    default:
      return 'No warranty recorded';
  }
}
